// Polyfill requestIdleCallback for environments that don't ship it
// (WKWebView in the macOS desktop app being the live offender — it
// crashes the boot the moment vendor analytics tries to schedule a
// beacon). The setTimeout fallback isn't idle-aware, but it's the
// well-known shim and matches the spec's "didTimeout: false / 0 ms
// remaining" contract.
if (typeof window !== "undefined" && typeof window.requestIdleCallback === "undefined") {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).requestIdleCallback = (cb: any) =>
    setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 0 }), 1);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).cancelIdleCallback = (id: any) => clearTimeout(id);
}

// Self-heal stale-deploy chunk errors. Lazy JS/CSS chunks are content-
// hashed, so after a deploy an ALREADY-OPEN tab's runtime references
// chunk files that no longer exist — the next lazy navigation throws
// "Loading (CSS) chunk N failed" and surfaces as a Runtime error page.
// The correct UX is a one-shot reload: the fresh HTML pulls the new
// runtime + chunk names. The sessionStorage guard (cleared after 10s of
// stable run) prevents a reload loop if the server itself is broken.
if (typeof window !== "undefined") {
  const RELOAD_GUARD = "chunk-error-reloaded";
  const isChunkError = (msg: unknown) => typeof msg === "string" && /Loading (CSS )?chunk [\w-]+ failed/i.test(msg);
  const healChunkError = () => {
    if (sessionStorage.getItem(RELOAD_GUARD)) return; // already tried — let the error surface
    sessionStorage.setItem(RELOAD_GUARD, "1");
    window.location.reload();
  };
  window.addEventListener("error", (e) => {
    if (isChunkError(e?.message) || isChunkError((e as ErrorEvent)?.error?.message)) healChunkError();
  });
  window.addEventListener("unhandledrejection", (e) => {
    if (isChunkError((e as PromiseRejectionEvent)?.reason?.message)) healChunkError();
  });
  setTimeout(() => sessionStorage.removeItem(RELOAD_GUARD), 10_000);
}

// Tag the document with the host shell so CSS can branch on it (e.g.
// Capacitor needs an explicit bottom breathing-room pad because its
// WebView reports ``safe-area-inset-bottom`` as 0 even when the
// gesture/nav bar is overlaying content). Mirrors the same classifier
// that runs on the Django-served login page in user_base.html so the
// React app and the auth pages share one body-class contract.
//
// On Capacitor specifically: if there's no JWT in localStorage and
// we're not already on a /auth/* route, hard-redirect to the React
// login. The bundled mobile app must NEVER fall through to Django's
// /user/login/ HTML page — that path serves a server-rendered
// template the offline bundle can't reach when disconnected. Doing
// the redirect at module-load time (before App.tsx mounts) avoids a
// flash of the unauthenticated SPA shell.
if (typeof window !== "undefined" && typeof document !== "undefined") {
  const apply = (cls: string) => {
    if (document.body) document.body.classList.add(cls);
    else document.addEventListener("DOMContentLoaded", () => document.body.classList.add(cls));
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const w = window as any;
  if (typeof w.Capacitor !== "undefined") {
    apply("is-capacitor");
    // Also apply the lsf-prefixed form so CSS rules authored inside
    // ``.prefix.css`` files as ``body.is-capacitor .foo {…}`` continue
    // to match after the css-loader's localIdentName transform. The
    // loader runs every class token in a .prefix.css selector through
    // ``lsf-[local]`` — including ``is-capacitor`` — so the compiled
    // CSS looks for ``body.lsf-is-capacitor``. The HTML template only
    // stamps the raw ``is-capacitor`` class, so 13 capacitor-scoped
    // rules (settings overflow cap, safe-area padding, tap-target
    // min-heights, …) silently matched nothing. Applying both class
    // names here fixes all of them without having to rewrite every
    // ``.prefix.css`` to escape the prefix.
    apply("lsf-is-capacitor");
    // Android hard-back handler. Default Capacitor behaviour exits the
    // app from ANY screen, which is the wrong UX for a SPA — users
    // expect back to navigate the history stack and only exit at the
    // root. The @capacitor/app plugin's "backButton" event fires
    // BEFORE the default handler; calling history.back() inside the
    // listener consumes the event and the app stays alive.
    //
    // Wrapped in try/catch so a misconfigured plugin (e.g. forgetting
    // to run ``cap sync`` after adding the dep) doesn't crash the SPA.
    // ``canGoBack`` from the event payload is set when there's stack
    // depth; we additionally check ``history.length > 1`` as the
    // browser-history fallback because the native event doesn't see
    // pushState routes the SPA does internally.
    try {
      const App = w.Capacitor?.Plugins?.App;
      if (App && typeof App.addListener === "function") {
        App.addListener("backButton", (event: { canGoBack: boolean }) => {
          if (event.canGoBack || window.history.length > 1) {
            window.history.back();
          } else if (typeof App.exitApp === "function") {
            App.exitApp();
          }
        });
      }
    } catch {
      /* plugin missing or native bridge not ready — Android will fall
         back to the default exit behaviour. Better than crashing. */
    }
    try {
      const path = window.location.pathname;
      // ``hasActiveSession`` checks more than presence: a JWT whose
      // ``exp`` is already in the past doesn't count as a session.
      // Without this, an expired token kept the boot path on the
      // authenticated branch — we'd render the menubar, fire the
      // first API call, the cloud would 401, ApiProvider would clear
      // the tokens and bounce to login. Visible flash of the
      // unauthenticated SPA shell. Decoding ``exp`` up-front skips
      // that round trip and lands the user on the auth screen on
      // first paint.
      //
      // We also accept "access expired but refresh still valid" as
      // an active session; the API layer will refresh on the first
      // 401 (or we'll add proactive refresh later). Without a
      // refresh token, an expired access is dead.
      const decodeExp = (token: string): number | null => {
        try {
          const payload = token.split(".")[1];
          if (!payload) return null;
          const json = atob(
            ((b) => {
              while (b.length % 4) b += "=";
              return b;
            })(payload.replace(/-/g, "+").replace(/_/g, "/")),
          );
          const claims = JSON.parse(json) as { exp?: number };
          return typeof claims.exp === "number" ? claims.exp : null;
        } catch {
          return null;
        }
      };
      const access = window.localStorage.getItem("afri:jwt:access");
      const refresh = window.localStorage.getItem("afri:jwt:refresh");
      const now = Math.floor(Date.now() / 1000);
      const accessExp = access ? decodeExp(access) : null;
      const refreshExp = refresh ? decodeExp(refresh) : null;
      const accessLive = accessExp !== null && accessExp > now;
      const refreshLive = refreshExp !== null && refreshExp > now;
      const hasActiveSession = accessLive || refreshLive;
      if (!hasActiveSession && !path.startsWith("/auth/")) {
        // Drop dead tokens so they don't linger and confuse the
        // ``hasTokens()`` checks elsewhere (e.g. the menubar's logout
        // branch, the offline-fetch outbox replay).
        if (access && !accessLive) window.localStorage.removeItem("afri:jwt:access");
        if (refresh && !refreshLive) window.localStorage.removeItem("afri:jwt:refresh");
        // ``history.replaceState`` (NOT ``location.replace``) so the
        // bundled SPA doesn't trigger a navigation request — the
        // WebView opens ``index.html`` and only that file exists in
        // ``webDir``; navigating to ``/auth/login`` as a fresh URL
        // would 404. ``replaceState`` just updates the URL bar in
        // place; React Router resolves the new path on mount and
        // renders the Login component without ever leaving the
        // already-loaded SPA shell.
        window.history.replaceState({}, "", "/auth/login");
      }
    } catch {
      /* localStorage disabled — let the API 401 handler fall back to
         the same redirect at first failed request. */
    }
  } else if (typeof w.__TAURI__ !== "undefined" || typeof w.__TAURI_INTERNALS__ !== "undefined") {
    apply("is-tauri");
  } else {
    apply("is-web");
  }
}

import { registerAnalytics } from "@humansignal/core";
registerAnalytics();

// Install the IndexedDB-backed offline write outbox. No-op on web
// + desktop (gated to ``body.is-capacitor`` inside). On mobile,
// failed writes to ``/api/annotations*`` and friends queue locally
// and drain on the next ``online`` event. See offline-fetch.ts +
// offline-outbox.ts for the contract; Step 5a of the mobile pivot.
import { installOfflineFetch } from "./utils/offline-fetch";
import { installExternalLinkHandler } from "./utils/helpers";
installOfflineFetch();
installExternalLinkHandler();

// Responsive tables: stamp data-label on every <table> cell so the
// phone card layout (app/responsive-tables.prefix.css) can label the
// values. All platforms — narrow browser windows benefit too.
import { installResponsiveTables } from "./utils/responsive-tables";
installResponsiveTables();

// Every dropdown opens the app's own scrolling list instead of the
// operating system's (see utils/select-popup.ts).
import { installSelectPopup } from "./utils/select-popup";
installSelectPopup();

// One clear screen when a rule of the organisation refuses the person
// (see utils/access-refused.ts), instead of an error per request.
import { installAccessRefused } from "./utils/access-refused";
import { installBuildCheck } from "./utils/build-check";
installAccessRefused();
// A tab left open across a deploy loads the new build at a safe moment.
installBuildCheck();

// P2 — media-cap eviction sweep on boot (Capacitor only). Deferred
// well past first paint; one IDB scan when the app is idle. Keeps
// the url_content blob store under the user's cap so the OS never
// nukes the whole origin (outbox included) for quota.
if (document.body?.classList?.contains("is-capacitor")) {
  window.setTimeout(() => {
    import("./utils/offline-storage")
      .then((m) => m.evictMediaOverCap())
      .then((r) => {
        if (r.evicted > 0) {
          // eslint-disable-next-line no-console
          console.info("[offline-storage] boot eviction", r);
        }
      })
      .catch(() => {
        /* best-effort */
      });
  }, 15_000);
}

import "./app/App";
import "./utils/service-worker";
import "./utils/state-registry-lso";
